import type { PortalLocale } from '../constants';
import { getStoredPortalLocale } from '../portalLocaleEvents';

export type MacInventoryStatus = 'authorized' | 'unknown' | 'blocked';

export type ScanMethodKey = 'arp' | 'nmapPing' | 'nmapPorts' | 'mdns' | 'snmp';

export interface MacInventoryMetaLabels {
  title: string;
  statusLabel: string;
  description: string;
  emptyMessage: string;
}

export interface AgentNetworkAdminPageLabels {
  sidebar: {
    catMain: string;
    catMac: string;
    catOps: string;
    dashboard: string;
    agents: string;
    spaces: string;
    checklist: string;
    blacklist: string;
    whitelist: string;
    scanJobs: string;
    isolationCriteria: string;
    settings: string;
  };
  menuIconAlt: {
    dashboard: string;
    agents: string;
    spaces: string;
    scanJobs: string;
    isolationCriteria: string;
    settings: string;
  };
  settings: {
    title: string;
    subtitle: string;
    editTags: string;
    tabIntegrations: string;
    tabAlerts: string;
    tabAgentKeys: string;
    tabScanPolicy: string;
    tabSimulator: string;
    tabControlledCheck: string;
    tabBas: string;
  };
  macInventory: {
    meta: Record<MacInventoryStatus, MacInventoryMetaLabels>;
    statusChanged: (mac: string, label: string) => string;
    noIseTarget: string;
    iseApply: string;
    iseClear: string;
    iseConfirm: (mac: string, label: string, policy: string) => string;
    iseDone: (label: string, status: string, mode: string) => string;
    iseFailed: (label: string) => string;
    statusChangeFailed: string;
    colStatusInline: string;
    colAutoTag: string;
    colUserTag: string;
    colBlockedReason: string;
    promoteTitle: string;
    promote: string;
    quarantine: string;
    release: string;
    needIseTarget: string;
    deletedCount: (n: number) => string;
    bulkDeleteFailed: string;
    tagsSyncDone: (synced: number, failed: number) => string;
    tagsSyncFailed: string;
    checklistRebuild: (created: number, updated: number, snapshots: number) => string;
    checklistRebuildFailed: string;
    addMac: string;
    bulkDelete: (n: number) => string;
    exportCsv: string;
    rebuildChecklist: string;
    syncTags: string;
    editTags: string;
    classifyHint: string;
    quickFilter: string;
    deleteTitle: (title: string) => string;
    deleteOneBody: (mac: string) => string;
    deleteUnknownNote: string;
    bulkDeleteTitle: string;
    bulkDeleteBody: (n: number) => string;
    bulkDeleteUnknownNote: string;
    classifyAria: string;
    classifyTo: (label: string) => string;
    currentClass: (label: string) => string;
  };
  integrations: {
    title: string;
    add: string;
    targetsTitle: (n: number) => string;
    targetsDesc: string;
    ancHistoryTitle: string;
    ancHistoryDesc: string;
    ancEmpty: string;
    syncHistoryTitle: string;
    syncHistoryDesc: string;
    syncEmpty: string;
    colLastSync: string;
    colSync: string;
    session: string;
    colStart: string;
    deleteConfirm: (name: string) => string;
    targetsFetchFailed: string;
    testOk: (msg: string) => string;
    testFail: (msg: string) => string;
    testFailed: string;
    iseMacSyncHint: string;
    splunkTitle: string;
    splunkDesc: string;
    splunkConfigured: string;
    splunkNotConfigured: string;
    splunkEmitting: string;
    splunkIdle: string;
    splunkIndex: (index: string) => string;
    splunkHecToggle: string;
    splunkTest: string;
    splunkTestOk: (msg: string) => string;
    splunkTestFail: (msg: string) => string;
    splunkStatusFailed: string;
    splunkOpenDashboard: string;
    splunkOpenHero: string;
    splunkOpenDemoSeed: string;
    splunkSeedHero: string;
    splunkSeedOk: (n: number) => string;
    splunkSeedFail: (msg: string) => string;
    splunkDemoBadge: string;
    splunkCachedBadge: string;
    splunkCachedHint: string;
    syncDryRun: (n: number) => string;
    syncDone: (added: number, status: string) => string;
    syncFailed: string;
    sessionsCount: (n: number) => string;
    sessionsFailed: string;
    dialogEdit: string;
    dialogAdd: string;
    dialogIseHint: string;
    dialogMockHint: string;
    iseMode: string;
    iseModeLive: string;
    iseModeMock: string;
    iseBaseUrlHint: string;
    tagFilter: string;
    tagFilterPlaceholder: string;
  };
  scanPolicy: {
    methods: Record<ScanMethodKey, string>;
    intervalSec: string;
    savedMsg: string;
    defaultIntervalTitle: string;
    defaultIntervalDesc: string;
    rogueHostnameHint: string;
    methodsTitle: string;
    methodsDesc: string;
    ouiRoutingHint: string;
    vendorRoutingEnabled: string;
    endpointPortScan: string;
    autoBasDryRun: string;
    snmpCollector: string;
  };
  simulator: Record<string, string | ((...args: never[]) => string)>;
  isolation: Record<string, string>;
  controlledCheck: Record<string, string>;
  bas: Record<string, string>;
  dashboard: Record<string, string | ((...args: number[]) => string)>;
  scanJobs: Record<string, string | ((triggered: number, total?: number) => string)>;
  alerts: Record<string, string | ((...args: number[]) => string)>;
  agentKeys: Record<string, string | ((label: string, keyId?: string) => string)>;
  spaces: Record<string, string | ((name: string) => string)>;
  whitelist: Record<string, string | ((...args: number[]) => string)>;
  agents: Record<string, string | ((triggered: number, total?: number, failed?: number) => string)>;
  tableActions: { header: string; edit: string; delete: string };
  scanAgentColumns: {
    presets: Record<string, string>;
    scanNow: string;
    scan: string;
  };
  crud: {
    spaceTypes: Record<string, string>;
    space: Record<string, string>;
    whitelist: Record<string, string>;
    agent: Record<string, string>;
    macForm: Record<string, string | ((...args: string[]) => string)>;
    tagEditor: Record<string, string | ((label: string) => string)>;
  };
  tagPresets: {
    space: string[];
    device: string[];
    agent: string[];
  };
}

const KO_MAC_META: Record<MacInventoryStatus, MacInventoryMetaLabels> = {
  authorized: {
    title: 'Whitelist',
    statusLabel: '인가',
    description: '인가된 MAC — Tag 편집 · Checklist/Blacklist로 이동 · 삭제',
    emptyMessage: 'Whitelist 항목이 없습니다.',
  },
  unknown: {
    title: 'Checklist',
    statusLabel: '미확인',
    description: '미확인 MAC — 발견 공간·Vendor 자동 Tag · Whitelist 승인/Blacklist 차단 · 삭제',
    emptyMessage: '미확인(Checklist) MAC이 없습니다.',
  },
  blocked: {
    title: 'Blacklist',
    statusLabel: '비인가',
    description: '비인가 MAC — 차단 사유 관리 · Checklist 재검토/Whitelist 승인 · 삭제',
    emptyMessage: 'Blacklist 항목이 없습니다.',
  },
};

const EN_MAC_META: Record<MacInventoryStatus, MacInventoryMetaLabels> = {
  authorized: {
    title: 'Whitelist',
    statusLabel: 'Authorized',
    description: 'Authorized MAC — edit tags · move to Checklist/Blacklist · delete',
    emptyMessage: 'No whitelist entries.',
  },
  unknown: {
    title: 'Checklist',
    statusLabel: 'Unknown',
    description: 'Unknown MAC — auto tags from space/vendor · approve to Whitelist or block · delete',
    emptyMessage: 'No checklist (unknown) MACs.',
  },
  blocked: {
    title: 'Blacklist',
    statusLabel: 'Unauthorized',
    description: 'Blocked MAC — block reason · recheck via Checklist or approve · delete',
    emptyMessage: 'No blacklist entries.',
  },
};

function buildKo(): AgentNetworkAdminPageLabels {
  return {
    sidebar: {
      catMain: '네트워크 자산관리',
      catMac: 'MAC 주소관리',
      catOps: '운영',
      dashboard: '대시보드',
      agents: 'Network Scanner 관리',
      spaces: '공간 관리',
      checklist: 'Checklist MAC 주소들',
      blacklist: 'Blacklist MAC 주소들',
      whitelist: 'Whitelist MAC 주소들',
      scanJobs: '스캔 이력',
      isolationCriteria: '격리기준 설정',
      settings: '설정',
    },
    menuIconAlt: {
      dashboard: '대시보드',
      agents: 'Network Scanner 관리',
      spaces: '공간 관리',
      scanJobs: '스캔 이력',
      isolationCriteria: '격리기준 설정',
      settings: '설정',
    },
    settings: {
      title: '설정',
      subtitle: 'NAC·장비 연동, Rogue 알림, ingest API Key, 스캔 정책, Simulator, Controlled-check, BAS를 관리합니다.',
      editTags: 'Tags 편집',
      tabIntegrations: '연동',
      tabAlerts: '알림',
      tabAgentKeys: 'API Keys',
      tabScanPolicy: 'Scan Policy',
      tabSimulator: 'Simulator',
      tabControlledCheck: 'Controlled-check',
      tabBas: 'BAS',
    },
    macInventory: {
      meta: KO_MAC_META,
      statusChanged: (mac, label) => `${mac} → ${label} 으로 변경됨`,
      noIseTarget: '활성 cisco_ise Integration Target이 없습니다 (Settings → NAC 연동).',
      iseApply: 'ISE 격리(ANC apply)',
      iseClear: 'ISE 격리 해제(ANC clear)',
      iseConfirm: (mac, label, policy) => `${mac}\n\n${label} 할까요?\npolicy=${policy}`,
      iseDone: (label, status, mode) => `${label} 완료 — ${status} (${mode})`,
      iseFailed: (label) => `${label} 실패`,
      statusChangeFailed: '상태 변경 실패',
      colStatusInline: '상태 (즉시 변경)',
      colAutoTag: '자동 Tag',
      colUserTag: '사용자 Tag',
      colBlockedReason: '차단 사유',
      promoteTitle: 'Governance 승격 제안 (AG-T2)',
      promote: '승격',
      quarantine: '격리',
      release: '해제',
      needIseTarget: 'cisco_ise Target 필요',
      deletedCount: (n) => `${n}건 삭제됨`,
      bulkDeleteFailed: '일괄 삭제 실패',
      tagsSyncDone: (synced, failed) =>
        `Tags 동기화 완료 — 성공 ${synced}건${failed ? ` · 실패 ${failed}건` : ''}`,
      tagsSyncFailed: 'Tags 동기화 실패',
      checklistRebuild: (created, updated, snapshots) =>
        `Checklist 재생성 — 생성 ${created} · 갱신 ${updated} (snapshot ${snapshots})`,
      checklistRebuildFailed: 'Checklist 재생성 실패',
      addMac: 'MAC 추가',
      bulkDelete: (n) => `선택 삭제 (${n})`,
      exportCsv: 'CSV 내보내기',
      rebuildChecklist: 'Checklist 재생성',
      syncTags: 'Tags 동기화',
      editTags: 'Tags 편집',
      classifyHint: '행의 인가 / 비인가 / 미확인 버튼을 누르면 편집 없이 즉시 분류됩니다.',
      quickFilter: 'MAC·Vendor·OUI·Tag·Hostname 검색',
      deleteTitle: (title) => `${title} 삭제`,
      deleteOneBody: (mac) => `MAC ${mac} 항목을 삭제합니다.`,
      deleteUnknownNote: ' 이후 다시 관측되면 Checklist 후보로 재생성될 수 있습니다.',
      bulkDeleteTitle: '선택 항목 삭제',
      bulkDeleteBody: (n) => `선택한 ${n}개 MAC을 삭제합니다.`,
      bulkDeleteUnknownNote: ' 재관측 시 Checklist 후보로 재생성될 수 있습니다.',
      classifyAria: 'MAC 인가 상태',
      classifyTo: (label) => `클릭 → ${label}`,
      currentClass: (label) => `현재: ${label}`,
    },
    integrations: {
      title: 'NAC · 장비 연동',
      add: '연동 추가',
      targetsTitle: (n) => `연동 대상 (${n})`,
      targetsDesc: 'DevNet · generic_rest / cisco_ise ANC — Whitelist Sync 또는 Blacklist 격리',
      ancHistoryTitle: 'ANC 이력 (Cisco ISE)',
      ancHistoryDesc: 'apply/clear job (최대 30건) — Blacklist에서 요청',
      ancEmpty: 'ANC 이력 없음',
      syncHistoryTitle: '동기화 이력',
      syncHistoryDesc: '최근 sync job (최대 30건)',
      syncEmpty: '동기화 이력 없음',
      colLastSync: '마지막 동기화',
      colSync: '동기화',
      session: '세션',
      colStart: '시작',
      deleteConfirm: (name) => `「${name}」 연동을 삭제할까요?`,
      targetsFetchFailed: '연동 대상 조회 실패',
      testOk: (msg) => `연결 테스트 성공: ${msg}`,
      testFail: (msg) => `실패: ${msg}`,
      testFailed: '테스트 실패',
      iseMacSyncHint: 'Cisco ISE는 MAC Sync 대신 Blacklist의 ANC 격리/해제·세션 조회를 사용합니다.',
      splunkTitle: 'Splunk HEC',
      splunkDesc: 'scan/rogue/ISE ANC 이벤트를 Splunk Cloud HEC로 전송 (example.env SPLUNK_HEC_*)',
      splunkConfigured: '자격증명 설정됨',
      splunkNotConfigured: 'SPLUNK_HEC_URL / TOKEN 미설정',
      splunkEmitting: '송신 ON',
      splunkIdle: '송신 OFF',
      splunkIndex: (index) => `index: ${index}`,
      splunkHecToggle: 'integrations.splunk.hec',
      splunkTest: 'HEC 테스트',
      splunkTestOk: (msg) => `HEC 성공: ${msg}`,
      splunkTestFail: (msg) => `HEC 실패: ${msg}`,
      splunkStatusFailed: 'Splunk 상태 조회 실패',
      splunkOpenDashboard: 'Splunk 대시보드 열기 (한글)',
      splunkOpenHero: '히어로 타임라인 검색',
      splunkOpenDemoSeed: '데모 시드 검색',
      splunkSeedHero: '히어로 타임라인 시드',
      splunkSeedOk: (n) => `히어로 타임라인 ${n}건 HEC 적재 완료`,
      splunkSeedFail: (msg) => `시드 실패: ${msg}`,
      splunkDemoBadge: 'demo-seed',
      splunkCachedBadge: 'cached',
      splunkCachedHint: 'HEC 연결 실패·만료(~14일) — last-good cache로 UI 유지',
      syncDryRun: (n) => `Dry-run: ${n} MAC 대상`,
      syncDone: (added, status) => `동기화 완료 — ${added} MAC push (${status})`,
      syncFailed: '동기화 실패',
      sessionsCount: (n) => `세션 ${n}건 (devnet/live)`,
      sessionsFailed: '세션 조회 실패',
      dialogEdit: '연동 수정',
      dialogAdd: '연동 추가',
      dialogIseHint: 'Cisco ISE — ANC는 Blacklist에서 실행. Base URL이 비면 ISE_BASE_URL(SSM)을 씁니다. live는 ISE /ers 에 닿을 때만.',
      dialogMockHint: 'Phase 2 Mock 커넥터 — 실제 vendor API는 Phase 3',
      iseMode: 'ISE 모드',
      iseModeLive: 'live (ERS)',
      iseModeMock: 'DevNet adapter',
      iseBaseUrlHint: '비우면 env/SSM ISE_BASE_URL. AnyConnect 예약 포털(:20195 CSCOE)은 ERS가 아닙니다.',
      tagFilter: 'Tag Filter (쉼표 구분)',
      tagFilterPlaceholder: '인가기기, 무선 AP',
    },
    scanPolicy: {
      methods: {
        arp: 'ARP 스캔',
        nmapPing: 'Nmap Ping Discovery',
        nmapPorts: 'Nmap Port Scan',
        mdns: 'mDNS',
        snmp: 'SNMP (레거시 토글 — Cisco는 Vendor routing + SNMP 사용)',
      },
      intervalSec: '주기(초)',
      savedMsg: '기본 스캔 정책이 저장되었습니다. Pi agent는 다음 config pull 시 반영됩니다.',
      defaultIntervalTitle: '기본 스캔 주기',
      defaultIntervalDesc: '모든 agent에 적용되는 기본값 (agent/subnet override 가능)',
      rogueHostnameHint: 'hostname 없는 신규 Rogue → medium severity',
      methodsTitle: '탐지 방법 (scanPolicy)',
      methodsDesc: 'Pi v0.2.0 agent가 ingest 시 source.methods로 보고',
      ouiRoutingHint:
        'ARP OUI 기준: Cisco → SNMP enrich · Intel/HP/Dell 등 endpoint → nmap ports · (선택) BAS dry-run 자동 큐',
      vendorRoutingEnabled: 'Vendor routing 사용',
      endpointPortScan: 'PC/서버 OUI → nmap port scan',
      autoBasDryRun: 'Port scan 후 BAS dry-run 자동 큐 (Admin offensive 게이트 별도)',
      snmpCollector: 'SNMP collector (Pi에 snmpget 필요)',
    },
    simulator: {
      title: 'lab-ops-v1 Virtual Probe — 현장형 Space · persona · Stage0–2 ingest',
      offHint:
        '꺼두면 주기 ingest·수동 스캔이 중지됩니다. 가상 응답만 사용하며 실익스플로잇은 없습니다. Pi 실 스캔 데이터는 계속 표시됩니다.',
      on: '시뮬레이터 켜짐',
      off: '시뮬레이터 꺼짐 (기본)',
      toggledOn: '시뮬레이터를 켰습니다.',
      toggledOff: '시뮬레이터를 껐습니다.',
      configSaved: '설정을 저장했습니다.',
      seedConfirm:
        'lab-ops-v1 시드로 Spaces/Whitelist/Agents를 교체합니다. Pi LAB은 유지됩니다. 계속할까요?',
      seedFailed: '시드 적용 실패',
      pipelineFailed: '파이프라인 실패',
      federationFailed: 'Federation sync 실패',
      sentrientFailed: 'Sentrient demo 적용 실패',
      policySaveFailed: 'Policy 저장 실패',
      policyResetFailed: 'Policy reset 실패',
      policyResetConfirm: 'Tenant policy를 삭제하고 demo default로 되돌릴까요?',
      opsNotes: '운영 메모',
      opsNotesPlaceholder: 'simulator 사용 목적·주의사항',
      saveConfig: '설정 저장',
      scenarioPack: 'lab-ops-v1 시나리오 팩',
      packLoading:
        '팩 메타를 불러오는 중이거나 미생성 상태입니다. generate-lab-ops-seed.mjs --write 후 사용하세요.',
      applySeed: 'Apply / Reset 시드',
      runStage: '지금 Stage0–2 실행',
      macTags: 'MAC→Tags 투영',
      actionQueueUser: '조치 필요 큐 (사용자)',
      policyJsonOpen: 'Business-Aware policy JSON',
      policyJsonClose: 'Policy JSON 닫기',
      policySavePut: 'Policy 저장 (PUT)',
      policyResetDemo: 'Demo default로 reset',
      changedAt: '변경',
      seedApplied: (spaceCount: number, wl: number, agents: number, cl = 0, bl = 0) =>
        `시드 적용: spaces=${spaceCount} WL=${wl} CL=${cl} BL=${bl} agents=${agents}`,
      virtualProbeDone: (triggered: number, total: number, portsSum: number) =>
        `Virtual Probe 완료: ${triggered}/${total} Spaces · openPorts 합계 ${portsSum}`,
      policySavedMsg: (ruleCount: number, source: string) =>
        `Policy 저장 · rules=${ruleCount} source=${source}`,
      labOpsToolsDesc:
        'lab-ops overlay · Quarantine ANC job · correlation fixture · 조치 큐(사용자 FE)',
    },
    isolation: {
      loading: '격리기준 설정 로딩…',
      title: '격리기준 설정',
      subtitle:
        'Scoring·격리·Critical·BAS 연계 기준 — 조치 필요 테이블 enrichment 및 P2 live BAS job 결과에 사용합니다.',
      savedAt: '저장:',
      savedDesc: 'tenant isolation-criteria.json 갱신',
      basTitle: 'BAS 연계',
      basDesc:
        '설정 탭 BAS Offensive playbook allowlist·RoE와 함께 동작합니다. mockMode=true 시 dry-run 요약만 조치 필요에 표시됩니다.',
      basEnrichment: '조치 필요 BAS enrichment',
      defaultPlaybook: '기본 playbook ID',
      openBasSettings: 'BAS Offensive 설정 열기',
      scoringTitle: 'Scoring 가중치',
      scoringDesc: 'Trust · Attackability · SBOM · Hygiene(AV/FW, 5%) composite. Hygiene는 Agent가 있을 때만 반영되며 자동 격리 단독 근거가 아닙니다.',
      hygienePercent: 'hygienePercent (AV/FW)',
      hygieneNote: 'AV 미설치·방화벽 중지만으로 자동 격리하지 않습니다. OT는 N/A, Agent 없으면 unknown.',
      quarantineTitle: '격리 (Quarantine)',
      autoQuarantineBands: '자동 격리 trust band (쉼표)',
      hitlBands: 'HITL trust band (쉼표)',
      autoQuarantineMinBas: '자동 격리 최소 BAS 점수',
      productionOtHitl: 'Production OT 항상 HITL',
      criticalTitle: 'Critical 기준',
      trustBelow: 'Trust 점수 이하',
      basAbove: 'BAS 점수 이상',
      sbomCriticalMin: 'SBOM Critical 최소 건수',
      reasonCodes: 'reason code (OR, 쉼표)',
    },
    controlledCheck: {
      loading: 'Controlled-check 설정 로딩…',
      subtitle: 'AI-N3 Safe path — 배너/TLS/포트 재확인만. BAS Offensive와 무관하며 exploit payload가 없습니다.',
      flagDesc: '테넌트 전역 Safe path 플래그. bas.offensive와 독립입니다.',
      allowFlag: 'Controlled-check 허용',
      roeTitle: 'NSA RoE (advise · controlled-check)',
      roeDesc: 'deny-by-default. modes에 controlled-check와 Space allowlist가 있어야 실행됩니다.',
      spaceAllowlist: 'Space allowlist',
      saveRoe: 'RoE 저장',
      noProbes: '프로브 없음',
      jobTitle: 'Controlled-check job 요청',
      jobDesc: '비파괴 재프로브만 큐에 넣습니다. Agent가 claim 후 simulation/result를 보고합니다.',
      targetsLabel: 'targets (쉼표/공백, 최대 16)',
      agentOptional: 'agentId (선택)',
      recentJobs: '최근 jobs',
      noJobs: 'job 없음',
      runRejected: '실행 거부',
      cancelFailed: '취소 실패',
      roeSaved: 'RoE 저장',
      roeSavedMsg: (modes: string, spaceCount: number) =>
        `RoE 저장 · modes=[${modes}] · spaces=${spaceCount}`,
    },
    bas: {
      loading: 'BAS 설정 로딩…',
      subtitle:
        'Breach and Attack Simulation — default OFF. admin 토글만으로 실행되지 않으며 다중 게이트가 필요합니다. Local은 dry-run만 기록합니다(실 exploit payload 없음).',
      flagDesc: '테넌트 전역 플래그. Agent capabilities.basOffensive에 반영됩니다.',
      allowOffensive: 'Offensive BAS 허용',
      killSwitch: '즉시 차단',
      allowlistDesc: '쉼표/공백 구분. 비어 있으면 deny-by-default.',
      saveAllowlist: 'Allowlist 저장',
      dryRunTitle: 'Dry-run job 요청 (P1 게이트)',
      dryRunDesc: '법적 침투시험 대체가 아닙니다. Agent가 dry-run만 수행합니다(payload 없음).',
      drySpacePlaceholder: 'allowlist에 있는 Space',
      confirmPlaceholder: '최소 8자',
      mfaPlaceholder: '최소 6자',
      enableConfirm:
        'BAS Offensive를 켭니다. admin 토글만으로는 실행되지 않으며 Space allowlist·playbook·HITL·blast 게이트가 필요합니다. 계속할까요?',
      toggledOn: 'bas.offensive = ON (다중 게이트 필수)',
      toggledOff: 'bas.offensive = OFF',
      allowlistSaved: 'Space allowlist 저장',
      allowlistSavedCount: (n: number) => `Space allowlist ${n}개 저장`,
      runRejected: '실행 거부',
    },
    dashboard: {
      title: '네트워크 관리 대시보드',
      healthOk: '정상',
      healthRogue: 'Rogue 탐지',
      healthWarn: '주의',
      subtitle: '공간 · 스캔 · Whitelist · Agent 상태를 한 화면에서 확인합니다.',
      screenRefresh: '화면 갱신',
      scanMap: '스캔 맵',
      autoRefresh: ' · 30초마다 자동 갱신',
      noSpacesTitle: '등록된 공간 없음',
      noSpacesDesc: '공간 탭에서 Location을 등록하거나 bootstrap 시드를 적용하세요.',
      kpiSpaces: '등록 공간',
      kpiSpacesHint: '관리 중인 Location 수',
      kpiScanDone: '스캔 완료',
      kpiCoverage: '커버리지',
      kpiWhitelistHint: '인가 등록 MAC',
      kpiObserved: '관측 기기',
      kpiObservedHint: '최신 스캔 snapshot 합계',
      kpiCompliance: '인가 / Rogue',
      kpiComplianceHint: '인가율',
      kpiAgents: 'Agent 온라인',
      kpiAgentsHint: '오프라인 · stale',
      scanCardTitle: '스캔 · 인가 현황',
      scanCardDesc: '공간 스캔 커버리지와 관측 기기 인가 비율',
      spaceScan: '공간 스캔',
      deviceAuth: '기기 인가',
      authorized: '인가',
      total: '전체',
      rogueAlert: '공간에서 Rogue',
      rogueAlertSuffix: '대가 탐지되었습니다. Whitelist 등록 또는 현장 확인이 필요합니다.',
      rogueAlertLine: (locCount: number, rogueCount: number) =>
        `${locCount}개 공간에서 Rogue ${rogueCount.toLocaleString('ko-KR')}대가 탐지되었습니다. Whitelist 등록 또는 현장 확인이 필요합니다.`,
      quickNavTitle: '빠른 이동',
      quickNavDesc: '자주 쓰는 관리 화면으로 바로 이동',
      rogueSpacesTitle: 'Rogue 다발 공간',
      rogueSpacesDesc: '스캔 snapshot 기준 Rogue 건수 상위 (최대 8)',
      noRogue: 'Rogue 없음',
      noRogueDesc: '모든 공간이 Whitelist 기준을 충족합니다.',
      colRatio: '비율',
      colScannedAt: '스캔 시각',
      agentsTitle: 'Agent 상태',
      agentsDesc: 'heartbeat 기준 상위 10건',
      noAgents: '등록된 Agent 없음',
      noAgentsDesc: 'simulator ingest를 실행하면 Agent가 표시됩니다.',
      splunkCardTitle: 'Splunk Digital Resilience',
      splunkCardDesc: 'HEC 연동 · 데모 시드 대시보드 (한글/영문 모드별 딥링크)',
      splunkOpen: 'Splunk 열기',
      splunkEmitting: 'HEC 송신 ON',
      splunkIdle: 'HEC 송신 OFF',
    },
    scanJobs: {
      title: '스캔 · 감사',
      triggerAll: '전체 스캔 트리거',
      triggerSuccess: '전체 스캔 트리거',
      triggerSuccessDetail: (triggered, total) => `전체 스캔 트리거: ${triggered}/${total} 성공`,
      triggerFailed: '스캔 트리거 실패',
      scanTab: '스캔 이력',
      auditTab: '감사 로그',
      kpiSubnetIndex: 'Subnet 인덱스',
      kpiSnapshots: 'Snapshot 보유',
      kpiStale: 'Stale (5분+)',
      latestSnapshotsTitle: '최신 ingest snapshot (subnet별)',
      latestSnapshotsDesc: 'GET /scans/latest — simulator·Pi ingest 이력',
      indexUpdated: '인덱스 갱신',
      scanFilter: '공간·subnet·agent 검색',
      scanEmpty: '스캔 이력 없음 — simulator ingest를 실행하세요.',
      auditTitle: '관리자 감사 로그',
      auditFilter: 'action·resource·user 검색',
      auditEmpty: '감사 이벤트 없음 — admin 작업 후 기록됩니다.',
      colScannedAt: '스캔 시각',
      colDevices: '기기',
      colResource: '리소스',
      colUser: '사용자',
      colDetail: '상세',
      alertScan: '스캔',
    },
    alerts: {
      title: 'Rogue 알림',
      settingsTitle: '알림 설정',
      settingsDesc:
        'ingest 신규 Rogue 탐지 시 이벤트 생성 · Email(SNS)/webhook dispatch (AWS: itops-networkagent-alerts-* SNS · 아래 Email 수신 주소가 구독 대상)',
      enableAlerts: '알림 활성화',
      rogueDetect: '신규 Rogue 탐지 시 알림',
      cooldown: 'Cooldown (분)',
      emailRecipients: 'Email 수신',
      historyTitle: '알림 이력',
      unacked: '미확인',
      ackHint: '건 · 「확인」 버튼으로 ack 처리',
      colSeverity: '심각도',
      colMessage: '메시지',
      colDetectedAt: '탐지 시각',
      colAck: '확인',
      acked: '확인됨',
      unackedBadge: '미확인',
      colWork: '작업',
      ack: '확인',
      filter: 'MAC·메시지 검색',
      empty: '알림 이력 없음 — ingest 시 신규 Rogue가 탐지되면 표시됩니다.',
      unackedLine: (n) => `미확인 ${n}건 · 「확인」 버튼으로 ack 처리`,
    },
    agentKeys: {
      subtitle:
        '스캔 에이전트 ingest 인증용 키를 발급·폐기합니다. Plain key는 발급 직후 1회만 표시됩니다.',
      plainOnce: 'Plain key는 발급 직후 1회만 표시됩니다.',
      issueDoneTitle: 'API Key 발급 완료',
      plainLabel: 'Plain Key (1회만 표시)',
      copy: '복사',
      confirm: '확인',
      revokeConfirm: '키를 폐기할까요?',
      revokeConfirmDetail: (label, keyId) => `키 "${label}" (${keyId})를 폐기할까요?`,
      revoked: '키가 폐기되었습니다.',
      revokeFailed: '폐기 실패',
      issueFailed: '발급 실패',
      needAgentId: 'agentId를 입력하세요.',
      enrollFailed: '등록 실패',
      colLabel: '라벨',
      colCreated: '생성',
      tabKeys: '일반 키',
      tabDevice: 'Device 키',
      issueKey: '키 발급',
      generalTitle: '일반 Ingest Keys',
      generalDesc: '시뮬레이터·공용 에이전트용',
      enrollDevice: 'Device 등록',
      deviceDesc: 'Raspberry Pi 등 장치별 키',
      issueDialogTitle: 'API Key 발급',
      issueDialogDesc: '새 ingest API key를 생성합니다.',
      labelField: '라벨',
      labelPlaceholder: '예: prod simulator',
      issue: '발급',
      enrollTitle: 'Device Agent 등록',
      enrollDesc: '기존 agentId에 device key를 발급(또는 재발급)합니다.',
      enroll: '등록',
    },
    spaces: {
      title: '공간(Space) 관리',
      add: '공간 추가',
      listTitle: '등록 공간',
      listDesc: 'CRUD · 통합 검색·유형/상태 필터·페이지네이션',
      filter: '이름·동/층·subnet 검색',
      empty: '등록된 공간이 없습니다.',
      deleteTitle: '공간 삭제',
      deleteBody: '을(를) 삭제합니다. 연관 whitelist의 space 참조가 제거됩니다.',
      deleteConfirmBody: (name) => `「${name}」을(를) 삭제합니다. 연관 whitelist의 space 참조가 제거됩니다.`,
    },
    whitelist: {
      addMac: 'MAC 추가',
      exportCsv: 'CSV 내보내기',
      importCsv: 'CSV 가져오기',
      syncing: '동기화 중…',
      syncTags: 'Tags 동기화',
      editTags: 'Tags 편집',
      importDone: '가져오기 완료',
      importResult: (created, updated, errors) =>
        `생성 ${created} · 갱신 ${updated} · 오류 ${errors}`,
      tagsSyncTitle: 'Tags 동기화',
      tagsSyncFailed: 'Tags 동기화 실패',
      listTitle: '허가 MAC',
      listDesc: 'CRUD · OUI/Vendor 정렬/필터 · CSV import/export',
      filter: 'MAC·hostname·vendor·OUI 검색',
      empty: 'Whitelist 항목이 없습니다.',
      deleteTitle: 'Whitelist 삭제',
      deleteBody: '항목을 삭제합니다.',
    },
    agents: {
      listTitle: '에이전트 목록',
      listDesc:
        'simulator·Pi heartbeat · 시뮬레이터 on/off는 설정 › Simulator에서 관리 · 전역 off 시 수동 스캔 불가',
      filter: 'Agent ID·Space·Subnet 검색',
      empty: '등록된 agent 없음 — simulator ingest 후 표시됩니다.',
      statusChangeFailed: 'Agent 상태 변경 실패',
      scanPartial: '스캔 성공 · 실패 (simulator :5199 확인)',
      scanDone: '스캔 완료',
      scanTriggerFailed: '스캔 트리거 실패',
      scanTriggerTitle: '스캔 트리거',
      scanTriggerPartial: (triggered, total, failed) =>
        `스캔 ${triggered}/${total} 성공 · 실패 ${failed} (simulator :5199 확인)`,
      scanTriggerDone: (triggered) => `스캔 완료 ${triggered}건`,
      scanNow: '지금 스캔',
    },
    tableActions: { header: '작업', edit: '수정', delete: '삭제' },
    scanAgentColumns: {
      presets: { summary: '요약', ops: '운영', all: '전체' },
      scanNow: '지금 스캔',
      scan: '스캔',
    },
    crud: {
      spaceTypes: {
        office: '사무실',
        meeting_room: '회의실',
        lobby: '로비',
        datacenter: 'IDC/NOC',
        lab: 'LAB / PoC',
        other: '기타',
      },
      space: {
        editTitle: '공간 수정',
        addTitle: '공간 추가',
        desc: '스캔 대상 공간(Location)을 등록합니다. Subnet CIDR과 구역 Tags를 함께 설정할 수 있습니다.',
        saveErrorTitle: '저장 오류',
        needNameId: '이름과 Location ID를 입력해 주세요.',
        basicInfo: '기본 정보',
        basicDesc: '공간 이름·유형·상태',
        name: '이름',
        namePlaceholder: '예: 본관 4층 회의실 51',
        type: '유형',
        status: '상태',
        activeItem: 'active — 스캔·표시',
        inactiveItem: 'inactive — 비활성',
        description: '설명',
        descPlaceholder: '운영 메모 (선택)',
        edgePoc: 'Edge Agent (PoC) 공간',
        edgePocHint: '라즈베리 Pi network agent ingest가 이 공간·subnet에 매칭됩니다. tenant당 하나만 지정됩니다.',
        location: '위치',
        locationDesc: '동·층 정보 — Location ID 자동 제안에 사용됩니다',
        building: '동',
        buildingPlaceholder: '예: 본관',
        floor: '층',
        floorPlaceholder: '예: 4F',
        idHint: '이름·동·층을 입력하면 ID가 자동 제안됩니다. 필요 시 직접 수정할 수 있습니다.',
        idcNode: 'IDC Graph 노드 (선택)',
        rackNode: 'Rack Graph 노드 (선택)',
        graphNote:
          'AN-V3: Network Space와 IDC Location Graph는 별도 축입니다. 선택적 FK만 저장하며 강제 병합하지 않습니다.',
        network: '네트워크',
        networkDesc: '스캔 대상 Subnet — CIDR 기준으로 게이트웨이·DHCP 범위가 자동 계산됩니다',
        scanActive: '스캔:',
        scanActiveVal: '활성',
        zoneTags: '구역 Tags',
        zoneTagsDesc: 'iam-tags와 별도 — 대시보드·스캔 필터용 라벨',
        zoneTagsHint: '예: 로비, 회의실, IDC — 빠른 추가 버튼 또는 직접 입력',
        footerEdit: '변경 사항을 저장합니다.',
        footerAdd: '저장 후 스캔 에이전트가 이 공간을 참조할 수 있습니다.',
        saveEdit: '변경 저장',
        register: '공간 등록',
      },
      whitelist: {
        editTitle: 'Whitelist 수정',
        addTitle: 'MAC 추가',
        desc: '허가 MAC을 등록하고 iam-tags network-devices Tag와 동기화합니다.',
        needMacSpace: '유효한 MAC 주소를 입력하고, location 범위일 경우 공간을 선택해 주세요.',
        identify: '기기 식별',
        identifyDesc: 'MAC 주소는 콜론(:) 구분 6옥텟 형식',
        macFormat: '형식: AA:BB:CC:DD:EE:FF (12자리 16진수)',
        ownerPlaceholder: '담당자·부서',
        scope: '적용 범위',
        scopeDesc: 'location: 특정 공간 한정 · global: 전체 공간',
        locationScope: 'location — 공간 한정',
        globalScope: 'global — 전체',
        pickSpace: '공간 선택',
        noSpaces: '등록된 공간 없음',
        globalHint: 'global 범위는 모든 공간에서 허가됩니다.',
        tagsSection: '메모 · iam-tags 분류',
        tagsSectionDesc: '저장 시 network-devices Tag로 동기화됩니다',
        notesPlaceholder: '본관 로비 — signage',
        notesLabel: '메모 (구역 — 장비유형)',
        tagLoading: 'iam-tags에서 Tag 로드 중…',
        tagHint: '분류 Tag — 저장 시 iam-tags에 반영됩니다.',
        syncTagsOnly: 'iam-tags만 동기화',
        openVisualizer: 'Tag Visualizer 열기',
        footerEdit: '저장 시 whitelist와 iam-tags Tag가 함께 갱신됩니다.',
        footerAdd: '등록 후 iam-tags network-devices Tag가 생성됩니다.',
        saveSync: '저장 · Tag 동기화',
        registerMac: 'MAC 등록',
      },
      agent: {
        title: 'Agent Tags · 메모',
        desc: 'heartbeat 메타는 ingest가 관리 · 운영 Tag만 편집',
        statusLine: '상태:',
        lastSeen: 'Last seen',
        linkedSpaceTags: '연결 공간 Tags (읽기 전용)',
        opsTagsHint: '스캐너 운영 분류 (simulator, production, edge 등)',
        opsNotes: '운영 메모',
      },
      macForm: {
        identify: '기기 식별',
        macFormat: '형식: AA:BB:CC:DD:EE:FF',
        ownerPlaceholder: '담당자·부서',
        itamTitle: 'ITAM 상관 (MAC↔AK)',
        itamDesc:
          'iam-assets Authority Key를 연결하면 tags·Twin enrich에 투영됩니다. 셀 write-back은 하지 않습니다.',
        displayNamePlaceholder: '표시명 (선택)',
        scopeTitle: '적용 범위',
        blockedReason: '차단 사유',
        blockedPlaceholder: '비인가 판정 근거',
        autoTags: '자동 시스템 Tag',
        autoTagsDesc:
          'Vendor 및 ARP 스캔 공간(Space) 기반으로 시스템이 자동 부여합니다. X를 눌러 숨길 수 있습니다.',
        noAutoTags: '표시할 자동 Tag 없음',
        hideTag: '숨기기',
        hidden: '숨김:',
        restore: '복원',
        userTags: '메모 · 분류 Tag',
        userTagsHint: '사용자 분류 Tag — 저장 시 iam-tags에 반영됩니다.',
        notesField: '메모',
        openVisualizer: 'Tag Visualizer 열기',
        needMacSpace: '유효한 MAC을 입력하고, location 범위일 경우 공간을 선택해 주세요.',
        editTitle: '수정',
        addTitle: 'MAC 추가',
        syncFooter: '저장 · Tag 동기화',
        register: 'MAC 등록',
        inventoryManageDesc: (statusLabel) =>
          `${statusLabel} 상태 MAC을 관리하고 iam-tags network-devices Tag와 동기화합니다.`,
        editMacTitle: (title) => `${title} 수정`,
        addMacTitle: (title) => `${title} MAC 추가`,
        restoreWithLabel: (label) => `${label} 복원`,
      },
      tagEditor: {
        title: '구역 · 분류 Tags',
        color: '색상',
        resetColor: '초기화',
        empty: '태그 없음 — 아래에서 추가',
        removeAria: (label) => `${label} 제거`,
        inputPlaceholder: '태그 입력 후 Enter (쉼표로 여러 개)',
        add: '추가',
      },
    },
    tagPresets: {
      space: ['로비', '공용', '회의실', '사무실', 'IDC', 'NOC', '보안구역'],
      device: [
        '인가기기',
        '데스크톱',
        '노트북',
        '프린터',
        '무선 AP',
        '디스플레이',
        '회의실',
        'IoT',
        '구역한정',
      ],
      agent: ['simulator', 'production', 'edge', '24x7', '점검필요'],
    },
  };
}

function buildEn(): AgentNetworkAdminPageLabels {
  const ko = buildKo();
  return {
    ...ko,
    sidebar: {
      catMain: 'Network asset management',
      catMac: 'MAC address management',
      catOps: 'Operations',
      dashboard: 'Dashboard',
      agents: 'Network scanner admin',
      spaces: 'Space management',
      checklist: 'Checklist MACs',
      blacklist: 'Blacklist MACs',
      whitelist: 'Whitelist MACs',
      scanJobs: 'Scan history',
      isolationCriteria: 'Isolation criteria',
      settings: 'Settings',
    },
    menuIconAlt: {
      dashboard: 'Dashboard',
      agents: 'Network scanner admin',
      spaces: 'Space management',
      scanJobs: 'Scan history',
      isolationCriteria: 'Isolation criteria',
      settings: 'Settings',
    },
    settings: {
      title: 'Settings',
      subtitle:
        'Manage NAC integrations, rogue alerts, ingest API keys, scan policy, simulator, controlled-check, and BAS.',
      editTags: 'Edit tags',
      tabIntegrations: 'Integrations',
      tabAlerts: 'Alerts',
      tabAgentKeys: 'API keys',
      tabScanPolicy: 'Scan policy',
      tabSimulator: 'Simulator',
      tabControlledCheck: 'Controlled-check',
      tabBas: 'BAS',
    },
    macInventory: {
      ...ko.macInventory,
      meta: EN_MAC_META,
      statusChanged: (mac, label) => `${mac} → ${label}`,
      noIseTarget: 'No active cisco_ise integration target (Settings → NAC integrations).',
      iseApply: 'ISE quarantine (ANC apply)',
      iseClear: 'ISE quarantine clear (ANC clear)',
      iseConfirm: (mac, label, policy) => `${mac}\n\nProceed with ${label}?\npolicy=${policy}`,
      iseDone: (label, status, mode) => `${label} done — ${status} (${mode})`,
      iseFailed: (label) => `${label} failed`,
      statusChangeFailed: 'Failed to change status',
      colStatusInline: 'Status (quick set)',
      colAutoTag: 'Auto tags',
      colUserTag: 'User tags',
      colBlockedReason: 'Block reason',
      promoteTitle: 'Governance promote (AG-T2)',
      promote: 'Promote',
      quarantine: 'Quarantine',
      release: 'Release',
      needIseTarget: 'cisco_ise target required',
      deletedCount: (n) => `${n} deleted`,
      bulkDeleteFailed: 'Bulk delete failed',
      tagsSyncDone: (synced, failed) =>
        `Tags synced — ${synced} OK${failed ? ` · ${failed} failed` : ''}`,
      tagsSyncFailed: 'Tags sync failed',
      checklistRebuild: (created, updated, snapshots) =>
        `Checklist rebuild — created ${created} · updated ${updated} (snapshots ${snapshots})`,
      checklistRebuildFailed: 'Checklist rebuild failed',
      addMac: 'Add MAC',
      bulkDelete: (n) => `Delete selected (${n})`,
      exportCsv: 'Export CSV',
      rebuildChecklist: 'Rebuild checklist',
      syncTags: 'Sync tags',
      editTags: 'Edit tags',
      classifyHint: 'Use Authorized / Unauthorized / Unknown buttons on each row to classify instantly.',
      quickFilter: 'Search MAC, vendor, OUI, tag, hostname',
      deleteTitle: (title) => `Delete ${title}`,
      deleteOneBody: (mac) => `Delete MAC entry ${mac}.`,
      deleteUnknownNote: ' It may reappear as a checklist candidate if observed again.',
      bulkDeleteTitle: 'Delete selected',
      bulkDeleteBody: (n) => `Delete ${n} selected MAC(s).`,
      bulkDeleteUnknownNote: ' May reappear as checklist candidates if observed again.',
      classifyAria: 'MAC authorization status',
      classifyTo: (label) => `Click → ${label}`,
      currentClass: (label) => `Current: ${label}`,
    },
    integrations: {
      title: 'NAC · device integrations',
      add: 'Add integration',
      targetsTitle: (n) => `Integration targets (${n})`,
      targetsDesc: 'DevNet · generic_rest / cisco_ise ANC — whitelist sync or blacklist quarantine',
      ancHistoryTitle: 'ANC history (Cisco ISE)',
      ancHistoryDesc: 'apply/clear jobs (max 30) — requested from Blacklist',
      ancEmpty: 'No ANC history',
      syncHistoryTitle: 'Sync history',
      syncHistoryDesc: 'Recent sync jobs (max 30)',
      syncEmpty: 'No sync history',
      colLastSync: 'Last sync',
      colSync: 'Sync',
      session: 'Sessions',
      colStart: 'Started',
      deleteConfirm: (name) => `Delete integration 「${name}」?`,
      targetsFetchFailed: 'Failed to load integration targets',
      testOk: (msg) => `Connection test OK: ${msg}`,
      testFail: (msg) => `Failed: ${msg}`,
      testFailed: 'Test failed',
      iseMacSyncHint: 'Cisco ISE uses Blacklist ANC quarantine/clear and session lookup instead of MAC sync.',
      splunkTitle: 'Splunk HEC',
      splunkDesc: 'Emit scan/rogue/ISE ANC events to Splunk Cloud HEC (example.env SPLUNK_HEC_*)',
      splunkConfigured: 'Credentials configured',
      splunkNotConfigured: 'SPLUNK_HEC_URL / TOKEN missing',
      splunkEmitting: 'Emitting ON',
      splunkIdle: 'Emitting OFF',
      splunkIndex: (index) => `index: ${index}`,
      splunkHecToggle: 'integrations.splunk.hec',
      splunkTest: 'Test HEC',
      splunkTestOk: (msg) => `HEC OK: ${msg}`,
      splunkTestFail: (msg) => `HEC failed: ${msg}`,
      splunkStatusFailed: 'Failed to load Splunk status',
      splunkOpenDashboard: 'Open Splunk dashboard (EN)',
      splunkOpenHero: 'Open hero timeline search',
      splunkOpenDemoSeed: 'Open demo-seed search',
      splunkSeedHero: 'Seed hero timeline',
      splunkSeedOk: (n) => `Hero timeline seeded (${n} events)`,
      splunkSeedFail: (msg) => `Seed failed: ${msg}`,
      splunkDemoBadge: 'demo-seed',
      splunkCachedBadge: 'cached',
      splunkCachedHint: 'HEC unreachable/expired (~14d) — UI served from last-good cache',
      syncDryRun: (n) => `Dry-run: ${n} MAC(s)`,
      syncDone: (added, status) => `Sync complete — ${added} MAC(s) pushed (${status})`,
      syncFailed: 'Sync failed',
      sessionsCount: (n) => `${n} session(s) (devnet/live)`,
      sessionsFailed: 'Session lookup failed',
      dialogEdit: 'Edit integration',
      dialogAdd: 'Add integration',
      dialogIseHint: 'Cisco ISE — run ANC from Blacklist. Empty Base URL uses ISE_BASE_URL (SSM). Live only when that URL serves /ers.',
      dialogMockHint: 'Phase 2 mock connector — real vendor API in Phase 3',
      iseMode: 'ISE mode',
      iseModeLive: 'live (ERS)',
      iseModeMock: 'DevNet adapter',
      iseBaseUrlHint: 'Leave empty to use env/SSM ISE_BASE_URL. Reservation AnyConnect (:20195 CSCOE) is not ERS.',
      tagFilter: 'Tag filter (comma-separated)',
      tagFilterPlaceholder: 'authorized device, wireless AP',
    },
    scanPolicy: {
      methods: {
        arp: 'ARP scan',
        nmapPing: 'Nmap ping discovery',
        nmapPorts: 'Nmap port scan',
        mdns: 'mDNS',
        snmp: 'SNMP (legacy toggle — Cisco uses vendor routing + SNMP)',
      },
      intervalSec: 'Interval (sec)',
      savedMsg: 'Default scan policy saved. Pi agents apply on next config pull.',
      defaultIntervalTitle: 'Default scan interval',
      defaultIntervalDesc: 'Default for all agents (agent/subnet override allowed)',
      rogueHostnameHint: 'New rogue without hostname → medium severity',
      methodsTitle: 'Detection methods (scanPolicy)',
      methodsDesc: 'Pi v0.2.0+ reports source.methods on ingest',
      ouiRoutingHint:
        'By ARP OUI: Cisco → SNMP enrich · Intel/HP/Dell endpoints → nmap ports · (optional) BAS dry-run queue',
      vendorRoutingEnabled: 'Use vendor routing',
      endpointPortScan: 'PC/server OUI → nmap port scan',
      autoBasDryRun: 'Auto BAS dry-run queue after port scan (separate offensive gate)',
      snmpCollector: 'SNMP collector (requires snmpget on Pi)',
    },
    simulator: {
      title: 'lab-ops-v1 Virtual Probe — field spaces · personas · Stage0–2 ingest',
      offHint:
        'When off, periodic ingest and manual scans stop. Virtual responses only; no real exploits. Pi live scan data still shows.',
      on: 'Simulator on',
      off: 'Simulator off (default)',
      toggledOn: 'Simulator enabled.',
      toggledOff: 'Simulator disabled.',
      configSaved: 'Settings saved.',
      seedConfirm: 'Replace Spaces/Whitelist/Agents with lab-ops-v1 seed. Pi LAB is kept. Continue?',
      seedFailed: 'Seed apply failed',
      pipelineFailed: 'Pipeline failed',
      federationFailed: 'Federation sync failed',
      sentrientFailed: 'Sentrient demo apply failed',
      policySaveFailed: 'Policy save failed',
      policyResetFailed: 'Policy reset failed',
      policyResetConfirm: 'Delete tenant policy and revert to demo default?',
      opsNotes: 'Ops notes',
      opsNotesPlaceholder: 'Simulator purpose and cautions',
      saveConfig: 'Save settings',
      scenarioPack: 'lab-ops-v1 scenario pack',
      packLoading: 'Loading pack meta or not generated. Run generate-lab-ops-seed.mjs --write first.',
      applySeed: 'Apply / reset seed',
      runStage: 'Run Stage0–2 now',
      macTags: 'Project MAC→Tags',
      actionQueueUser: 'Action queue (user FE)',
      policyJsonOpen: 'Business-Aware policy JSON',
      policyJsonClose: 'Close policy JSON',
      policySavePut: 'Save policy (PUT)',
      policyResetDemo: 'Reset to demo default',
      changedAt: 'Changed',
      seedApplied: (spaceCount: number, wl: number, agents: number, cl = 0, bl = 0) =>
        `Seed applied: spaces=${spaceCount} WL=${wl} CL=${cl} BL=${bl} agents=${agents}`,
      virtualProbeDone: (triggered: number, total: number, portsSum: number) =>
        `Virtual Probe done: ${triggered}/${total} spaces · openPorts total ${portsSum}`,
      policySavedMsg: (ruleCount: number, source: string) =>
        `Policy saved · rules=${ruleCount} source=${source}`,
      labOpsToolsDesc:
        'lab-ops overlay · Quarantine ANC job · correlation fixture · action queue (user FE)',
    },
    isolation: {
      loading: 'Loading isolation criteria…',
      title: 'Isolation criteria',
      subtitle: 'Scoring, quarantine, critical, and BAS rules for action-required enrichment.',
      savedAt: 'Saved:',
      savedDesc: 'tenant isolation-criteria.json updated',
      basTitle: 'BAS integration',
      basDesc: 'Works with BAS offensive playbook allowlist and RoE. mockMode=true shows dry-run summary only.',
      basEnrichment: 'Action-required BAS enrichment',
      defaultPlaybook: 'Default playbook ID',
      openBasSettings: 'Open BAS offensive settings',
      scoringTitle: 'Scoring weights',
      scoringDesc: 'Trust · attackability · SBOM · hygiene (AV/FW, 5%) composite. Hygiene applies only with System Agent and never auto-quarantines by itself.',
      hygienePercent: 'hygienePercent (AV/FW)',
      hygieneNote: 'Antivirus off or host firewall off never auto-quarantines. OT is N/A; missing Agent is unknown.',
      quarantineTitle: 'Quarantine',
      autoQuarantineBands: 'Auto-quarantine trust bands (comma)',
      hitlBands: 'HITL trust bands (comma)',
      autoQuarantineMinBas: 'Min BAS score for auto-quarantine',
      productionOtHitl: 'Production OT always HITL',
      criticalTitle: 'Critical criteria',
      trustBelow: 'Trust score at or below',
      basAbove: 'BAS score at or above',
      sbomCriticalMin: 'Min SBOM critical count',
      reasonCodes: 'Reason codes (OR, comma)',
    },
    controlledCheck: {
      loading: 'Loading controlled-check settings…',
      subtitle: 'AI-N3 safe path — banner/TLS/port recheck only. No exploit payload; independent of BAS offensive.',
      flagDesc: 'Tenant-wide safe-path flag. Independent of bas.offensive.',
      allowFlag: 'Allow controlled-check',
      roeTitle: 'NSA RoE (advise · controlled-check)',
      roeDesc: 'Deny-by-default. modes must include controlled-check and Space allowlist to run.',
      spaceAllowlist: 'Space allowlist',
      saveRoe: 'Save RoE',
      noProbes: 'No probes',
      jobTitle: 'Controlled-check job request',
      jobDesc: 'Queues non-destructive reprobes. Agent reports simulation/result after claim.',
      targetsLabel: 'Targets (comma/space, max 16)',
      agentOptional: 'agentId (optional)',
      recentJobs: 'Recent jobs',
      noJobs: 'No jobs',
      runRejected: 'Run rejected',
      cancelFailed: 'Cancel failed',
      roeSaved: 'RoE saved',
      roeSavedMsg: (modes: string, spaceCount: number) =>
        `RoE saved · modes=[${modes}] · spaces=${spaceCount}`,
    },
    bas: {
      loading: 'Loading BAS settings…',
      subtitle:
        'Breach and attack simulation — default OFF. Multiple gates required; local records dry-run only (no exploit payload).',
      flagDesc: 'Tenant flag. Reflected in Agent capabilities.basOffensive.',
      allowOffensive: 'Allow offensive BAS',
      killSwitch: 'Kill switch',
      allowlistDesc: 'Comma/space separated. Empty = deny-by-default.',
      saveAllowlist: 'Save allowlist',
      dryRunTitle: 'Dry-run job request (P1 gate)',
      dryRunDesc: 'Not a penetration test substitute. Agent performs dry-run only (no payload).',
      drySpacePlaceholder: 'Space on allowlist',
      confirmPlaceholder: 'Min 8 chars',
      mfaPlaceholder: 'Min 6 chars',
      enableConfirm:
        'Enable BAS offensive. Requires allowlist, playbook, HITL, and blast gates. Continue?',
      toggledOn: 'bas.offensive = ON (gates required)',
      toggledOff: 'bas.offensive = OFF',
      allowlistSaved: 'Space allowlist saved',
      allowlistSavedCount: (n: number) => `Space allowlist saved (${n})`,
      runRejected: 'Run rejected',
    },
    dashboard: {
      title: 'Network admin dashboard',
      healthOk: 'Healthy',
      healthRogue: 'Rogue detected',
      healthWarn: 'Attention',
      subtitle: 'Spaces, scans, whitelist, and agent status at a glance.',
      screenRefresh: 'Screen refresh',
      scanMap: 'Scan map',
      autoRefresh: ' · auto refresh every 30s',
      noSpacesTitle: 'No spaces registered',
      noSpacesDesc: 'Register locations under Spaces or apply bootstrap seed.',
      kpiSpaces: 'Registered spaces',
      kpiSpacesHint: 'Managed locations',
      kpiScanDone: 'Scan complete',
      kpiCoverage: 'Coverage',
      kpiWhitelistHint: 'Authorized MACs registered',
      kpiObserved: 'Observed devices',
      kpiObservedHint: 'Latest scan snapshot total',
      kpiCompliance: 'Authorized / rogue',
      kpiComplianceHint: 'Compliance',
      kpiAgents: 'Agents online',
      kpiAgentsHint: 'offline · stale',
      scanCardTitle: 'Scan & authorization',
      scanCardDesc: 'Space scan coverage and observed device authorization rate',
      spaceScan: 'Space scan',
      deviceAuth: 'Device authorization',
      authorized: 'Authorized',
      total: 'Total',
      rogueAlert: 'Rogue detected in',
      rogueAlertSuffix: 'space(s). Register whitelist or verify on site.',
      rogueAlertLine: (locCount: number, rogueCount: number) =>
        `Rogue ${rogueCount.toLocaleString()} device(s) in ${locCount} space(s). Register whitelist or verify on site.`,
      quickNavTitle: 'Quick links',
      quickNavDesc: 'Jump to common admin screens',
      rogueSpacesTitle: 'Top rogue spaces',
      rogueSpacesDesc: 'Top 8 spaces by rogue count (scan snapshot)',
      noRogue: 'No rogue devices',
      noRogueDesc: 'All spaces meet whitelist criteria.',
      colRatio: 'Ratio',
      colScannedAt: 'Scanned at',
      agentsTitle: 'Agent status',
      agentsDesc: 'Top 10 by heartbeat',
      noAgents: 'No agents registered',
      noAgentsDesc: 'Run simulator ingest to see agents.',
      splunkCardTitle: 'Splunk Digital Resilience',
      splunkCardDesc: 'HEC status · demo-seed dashboard (EN/KO deep links follow portal locale)',
      splunkOpen: 'Open Splunk',
      splunkEmitting: 'HEC emitting ON',
      splunkIdle: 'HEC emitting OFF',
    },
    scanJobs: {
      title: 'Scan & audit',
      triggerAll: 'Trigger full scan',
      triggerSuccess: 'Full scan triggered',
      triggerSuccessDetail: (triggered, total) => `Full scan triggered: ${triggered}/${total} succeeded`,
      triggerFailed: 'Scan trigger failed',
      scanTab: 'Scan history',
      auditTab: 'Audit log',
      kpiSubnetIndex: 'Subnet index',
      kpiSnapshots: 'With snapshot',
      kpiStale: 'Stale (5m+)',
      latestSnapshotsTitle: 'Latest ingest snapshots (by subnet)',
      latestSnapshotsDesc: 'GET /scans/latest — simulator and Pi ingest history',
      indexUpdated: 'Index updated',
      scanFilter: 'Search space, subnet, agent',
      scanEmpty: 'No scan history — run simulator ingest.',
      auditTitle: 'Admin audit log',
      auditFilter: 'Search action, resource, user',
      auditEmpty: 'No audit events — recorded after admin actions.',
      colScannedAt: 'Scanned at',
      colDevices: 'Devices',
      colResource: 'Resource',
      colUser: 'User',
      colDetail: 'Detail',
      alertScan: 'Scan',
    },
    alerts: {
      title: 'Rogue alerts',
      settingsTitle: 'Alert settings',
      settingsDesc:
        'Events on new rogue detection · email (SNS)/webhook dispatch (AWS: itops-networkagent-alerts-* SNS)',
      enableAlerts: 'Enable alerts',
      rogueDetect: 'Alert on new rogue detection',
      cooldown: 'Cooldown (min)',
      emailRecipients: 'Email recipients',
      historyTitle: 'Alert history',
      unacked: 'Unacknowledged',
      ackHint: ' · use Ack button',
      colSeverity: 'Severity',
      colMessage: 'Message',
      colDetectedAt: 'Detected at',
      colAck: 'Ack',
      acked: 'Acknowledged',
      unackedBadge: 'Open',
      colWork: 'Actions',
      ack: 'Ack',
      filter: 'Search MAC or message',
      empty: 'No alert history — appears when new rogues are detected on ingest.',
      unackedLine: (n) => `${n} unacknowledged · use Ack button`,
    },
    agentKeys: {
      subtitle: 'Issue and revoke ingest API keys for scan agents. Plain key shown once after issue.',
      plainOnce: 'Plain key is shown only once after issue.',
      issueDoneTitle: 'API key issued',
      plainLabel: 'Plain key (one-time)',
      copy: 'Copy',
      confirm: 'OK',
      revokeConfirm: 'Revoke this key?',
      revokeConfirmDetail: (label, keyId) => `Revoke key "${label}" (${keyId})?`,
      revoked: 'Key revoked.',
      revokeFailed: 'Revoke failed',
      issueFailed: 'Issue failed',
      needAgentId: 'Enter agentId.',
      enrollFailed: 'Enrollment failed',
      colLabel: 'Label',
      colCreated: 'Created',
      tabKeys: 'General keys',
      tabDevice: 'Device keys',
      issueKey: 'Issue key',
      generalTitle: 'General ingest keys',
      generalDesc: 'Simulator and shared agents',
      enrollDevice: 'Register device',
      deviceDesc: 'Per-device keys (e.g. Raspberry Pi)',
      issueDialogTitle: 'Issue API key',
      issueDialogDesc: 'Create a new ingest API key.',
      labelField: 'Label',
      labelPlaceholder: 'e.g. prod simulator',
      issue: 'Issue',
      enrollTitle: 'Register device agent',
      enrollDesc: 'Issue (or re-issue) device key for an existing agentId.',
      enroll: 'Register',
    },
    spaces: {
      title: 'Space management',
      add: 'Add space',
      listTitle: 'Registered spaces',
      listDesc: 'CRUD · search · type/status filters · pagination',
      filter: 'Search name, building/floor, subnet',
      empty: 'No spaces registered.',
      deleteTitle: 'Delete space',
      deleteBody: ' will be deleted. Whitelist space references are removed.',
      deleteConfirmBody: (name) => `Delete 「${name}」. Whitelist space references are removed.`,
    },
    whitelist: {
      addMac: 'Add MAC',
      exportCsv: 'Export CSV',
      importCsv: 'Import CSV',
      syncing: 'Syncing…',
      syncTags: 'Sync tags',
      editTags: 'Edit tags',
      importDone: 'Import complete',
      importResult: (created, updated, errors) =>
        `Created ${created} · updated ${updated} · errors ${errors}`,
      tagsSyncTitle: 'Tags sync',
      tagsSyncFailed: 'Tags sync failed',
      listTitle: 'Authorized MACs',
      listDesc: 'CRUD · OUI/vendor sort/filter · CSV import/export',
      filter: 'Search MAC, hostname, vendor, OUI',
      empty: 'No whitelist entries.',
      deleteTitle: 'Delete whitelist entry',
      deleteBody: ' entry will be deleted.',
    },
    agents: {
      listTitle: 'Agent list',
      listDesc: 'Simulator/Pi heartbeat · simulator toggle under Settings › Simulator · manual scan disabled when globally off',
      filter: 'Search agent ID, space, subnet',
      empty: 'No agents — appears after simulator ingest.',
      statusChangeFailed: 'Failed to change agent status',
      scanPartial: 'scan OK · failures (check simulator :5199)',
      scanDone: 'Scan complete',
      scanTriggerFailed: 'Scan trigger failed',
      scanTriggerTitle: 'Scan trigger',
      scanTriggerPartial: (triggered, total, failed) =>
        `Scan ${triggered}/${total} OK · ${failed} failed (check simulator :5199)`,
      scanTriggerDone: (triggered) => `Scan complete (${triggered})`,
      scanNow: 'Scan now',
    },
    tableActions: { header: 'Actions', edit: 'Edit', delete: 'Delete' },
    scanAgentColumns: {
      presets: { summary: 'Summary', ops: 'Operations', all: 'All' },
      scanNow: 'Scan now',
      scan: 'Scan',
    },
    crud: {
      spaceTypes: {
        office: 'Office',
        meeting_room: 'Meeting room',
        lobby: 'Lobby',
        datacenter: 'IDC/NOC',
        lab: 'LAB / PoC',
        other: 'Other',
      },
      space: {
        editTitle: 'Edit space',
        addTitle: 'Add space',
        desc: 'Register a scan target location. Set subnet CIDR and zone tags.',
        saveErrorTitle: 'Save error',
        needNameId: 'Enter name and location ID.',
        basicInfo: 'Basic info',
        basicDesc: 'Name, type, status',
        name: 'Name',
        namePlaceholder: 'e.g. Building A 4F Room 51',
        type: 'Type',
        status: 'Status',
        activeItem: 'active — scan & display',
        inactiveItem: 'inactive — disabled',
        description: 'Description',
        descPlaceholder: 'Ops notes (optional)',
        edgePoc: 'Edge agent (PoC) space',
        edgePocHint: 'Pi network agent ingest maps to this space/subnet. One per tenant.',
        location: 'Location',
        locationDesc: 'Building/floor — used for location ID suggestion',
        building: 'Building',
        buildingPlaceholder: 'e.g. Building A',
        floor: 'Floor',
        floorPlaceholder: 'e.g. 4F',
        idHint: 'ID is suggested from name/building/floor. Edit manually if needed.',
        idcNode: 'IDC graph node (optional)',
        rackNode: 'Rack graph node (optional)',
        graphNote: 'AN-V3: Network space and IDC graph are separate; optional FK only.',
        network: 'Network',
        networkDesc: 'Scan subnets — gateway/DHCP derived from CIDR',
        scanActive: 'Scan:',
        scanActiveVal: 'active',
        zoneTags: 'Zone tags',
        zoneTagsDesc: 'Separate from iam-tags — dashboard and scan filters',
        zoneTagsHint: 'e.g. lobby, meeting room, IDC — presets or custom',
        footerEdit: 'Save changes.',
        footerAdd: 'After save, scan agents can reference this space.',
        saveEdit: 'Save changes',
        register: 'Register space',
      },
      whitelist: {
        editTitle: 'Edit whitelist',
        addTitle: 'Add MAC',
        desc: 'Register authorized MAC and sync iam-tags network-devices tag.',
        needMacSpace: 'Enter valid MAC and select space for location scope.',
        identify: 'Device identity',
        identifyDesc: 'MAC in colon-separated 6-octet form',
        macFormat: 'Format: AA:BB:CC:DD:EE:FF',
        ownerPlaceholder: 'Owner or department',
        scope: 'Scope',
        scopeDesc: 'location: one space · global: all spaces',
        locationScope: 'location — one space',
        globalScope: 'global — all',
        pickSpace: 'Select space',
        noSpaces: 'No spaces registered',
        globalHint: 'Global scope applies in all spaces.',
        tagsSection: 'Notes · iam-tags classification',
        tagsSectionDesc: 'Syncs to network-devices tag on save',
        notesPlaceholder: 'Lobby — signage',
        notesLabel: 'Notes (zone — device type)',
        tagLoading: 'Loading tag from iam-tags…',
        tagHint: 'Classification tags — saved to iam-tags',
        syncTagsOnly: 'Sync iam-tags only',
        openVisualizer: 'Open Tag Visualizer',
        footerEdit: 'Updates whitelist and iam-tags tag together.',
        footerAdd: 'Creates iam-tags network-devices tag after register.',
        saveSync: 'Save · sync tags',
        registerMac: 'Register MAC',
      },
      agent: {
        title: 'Agent tags · notes',
        desc: 'Heartbeat meta managed by ingest · edit ops tags only',
        statusLine: 'Status:',
        lastSeen: 'Last seen',
        linkedSpaceTags: 'Linked space tags (read-only)',
        opsTagsHint: 'Scanner ops labels (simulator, production, edge, etc.)',
        opsNotes: 'Ops notes',
      },
      macForm: {
        identify: 'Device identity',
        macFormat: 'Format: AA:BB:CC:DD:EE:FF',
        ownerPlaceholder: 'Owner or department',
        itamTitle: 'ITAM correlation (MAC↔AK)',
        itamDesc: 'Link iam-assets authority key for tag/twin enrich. No cell write-back.',
        displayNamePlaceholder: 'Display name (optional)',
        scopeTitle: 'Scope',
        blockedReason: 'Block reason',
        blockedPlaceholder: 'Unauthorized classification reason',
        autoTags: 'Auto system tags',
        autoTagsDesc: 'From vendor and ARP scan space. Hide with X.',
        noAutoTags: 'No auto tags to show',
        hideTag: 'Hide',
        hidden: 'Hidden:',
        restore: 'Restore',
        userTags: 'Notes · classification tags',
        userTagsHint: 'User tags — saved to iam-tags',
        notesField: 'Notes',
        openVisualizer: 'Open Tag Visualizer',
        needMacSpace: 'Enter valid MAC and select space for location scope.',
        editTitle: 'Edit',
        addTitle: 'Add MAC',
        syncFooter: 'Save · sync tags',
        register: 'Register MAC',
        inventoryManageDesc: (statusLabel) =>
          `Manage ${statusLabel} MAC entries and sync iam-tags network-devices tag.`,
        editMacTitle: (title) => `Edit ${title}`,
        addMacTitle: (title) => `Add ${title} MAC`,
        restoreWithLabel: (label) => `Restore ${label}`,
      },
      tagEditor: {
        title: 'Zone · classification tags',
        color: 'Color',
        resetColor: 'Reset',
        empty: 'No tags — add below',
        removeAria: (label) => `Remove ${label}`,
        inputPlaceholder: 'Type tag and Enter (comma-separated)',
        add: 'Add',
      },
    },
    tagPresets: {
      space: ['Lobby', 'Common', 'Meeting room', 'Office', 'IDC', 'NOC', 'Secure zone'],
      device: [
        'Authorized device',
        'Desktop',
        'Laptop',
        'Printer',
        'Wireless AP',
        'Display',
        'Meeting room',
        'IoT',
        'Zone restricted',
      ],
      agent: ['simulator', 'production', 'edge', '24x7', 'needs maintenance'],
    },
  };
}

const LABELS: Record<PortalLocale, AgentNetworkAdminPageLabels> = {
  ko: buildKo(),
  en: buildEn(),
};

export function getAgentNetworkAdminPageLabels(locale?: PortalLocale): AgentNetworkAdminPageLabels {
  return LABELS[locale ?? getStoredPortalLocale()];
}

export function getMacInventoryMetaLabels(
  locale?: PortalLocale,
): Record<MacInventoryStatus, MacInventoryMetaLabels> {
  return getAgentNetworkAdminPageLabels(locale).macInventory.meta;
}

export function macInventoryStatusLabel(
  status: MacInventoryStatus | undefined,
  locale?: PortalLocale,
): string {
  if (!status) return '—';
  return getMacInventoryMetaLabels(locale)[status]?.statusLabel ?? status;
}

export function getScanMethodLabels(locale?: PortalLocale): Record<ScanMethodKey, string> {
  return getAgentNetworkAdminPageLabels(locale).scanPolicy.methods;
}
